import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Bot, Send, Clock, Calendar } from 'lucide-react';

const DEFAULT_CFG = {
  followup_enabled: true, followup_wait_days: 3, followup_template_id: null,
  auto_close_enabled: false, auto_close_days: 30,
  weekly_reminder_enabled: false, weekly_reminder_stale_days: 7,
};

function Toggle({ checked, onChange, testid }) {
  return (
    <label className="inline-flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={testid} className="w-5 h-5" />
      <span className="text-sm">{checked ? 'On' : 'Off'}</span>
    </label>
  );
}

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="border border-[#E5D9C8] rounded-sm bg-white p-6 space-y-4">
      <div className="flex items-start gap-3">
        <Icon className="w-5 h-5 text-[#C5A059] mt-1 flex-shrink-0" />
        <div className="flex-1">
          <div className="font-serif text-lg">{title}</div>
          <p className="text-sm text-[#3D2817]/60 mt-0.5">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

export default function AutomationsTab() {
  const [cfg, setCfg] = useState(DEFAULT_CFG);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/admin/automations'), api.get('/admin/reply-templates')])
      .then(([a, t]) => { setCfg({ ...DEFAULT_CFG, ...a.data }); setTemplates(t.data); }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put('/admin/automations', cfg); toast.success('Automations saved'); }
    catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/admin/run-followups');
      const f = data.followups;
      const parts = [];
      if (f.disabled) parts.push('follow-ups off');
      else parts.push(`${f.sent} sent, ${f.skipped_no_api_key} no-api-key, ${f.skipped_no_email} no-email, ${f.errors} errors`);
      parts.push(`auto-closed ${data.auto_close.closed || 0}`);
      parts.push(`monday ${data.monday_reminder.skipped ? 'skipped (' + data.monday_reminder.reason + ')' : (data.monday_reminder.sent ? 'sent' : 'failed')}`);
      toast.success(parts.join(' · '));
    } catch { toast.error('Run failed'); }
    finally { setRunning(false); }
  };

  return (
    <div>
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.3em] text-[#C5A059] mb-2">— Automations</div>
        <h2 className="font-serif text-3xl">Rules & Reminders</h2>
        <p className="text-sm text-[#3D2817]/60 mt-1">Configure automatic actions so nothing falls through the cracks.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl">
        <Section icon={Send} title="Follow-up emails" subtitle="Email buyers whose quotes stay in 'new' status too long.">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#3D2817]/60">Enabled</span>
            <Toggle checked={cfg.followup_enabled} onChange={v => setCfg({ ...cfg, followup_enabled: v })} testid="followup-enabled" />
          </div>
          <div>
            <Label>Wait days</Label>
            <Input data-testid="followup-days" type="number" min="1" max="30" className="max-w-[120px] mt-1"
              value={cfg.followup_wait_days} onChange={e => setCfg({ ...cfg, followup_wait_days: parseInt(e.target.value) || 1 })} />
          </div>
          <div>
            <Label>Template</Label>
            <Select value={cfg.followup_template_id || '__default__'} onValueChange={v => setCfg({ ...cfg, followup_template_id: v === '__default__' ? null : v })}>
              <SelectTrigger data-testid="followup-template" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Default follow-up</SelectItem>
                {templates.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </Section>

        <Section icon={Clock} title="Auto-close stale quotes" subtitle="Close quotes still in 'new' status after N days to keep the pipeline tidy.">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#3D2817]/60">Enabled</span>
            <Toggle checked={cfg.auto_close_enabled} onChange={v => setCfg({ ...cfg, auto_close_enabled: v })} testid="autoclose-enabled" />
          </div>
          <div>
            <Label>Days before auto-close</Label>
            <Input data-testid="autoclose-days" type="number" min="7" max="365" className="max-w-[120px] mt-1"
              value={cfg.auto_close_days} onChange={e => setCfg({ ...cfg, auto_close_days: parseInt(e.target.value) || 30 })} />
            <p className="text-xs text-[#3D2817]/50 mt-1">Closed quotes get reason "auto_closed_stale" and appear in KPI conversion.</p>
          </div>
        </Section>

        <Section icon={Calendar} title="Monday morning reminder" subtitle="Email yourself every Monday with in-progress deals gone quiet.">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[#3D2817]/60">Enabled</span>
            <Toggle checked={cfg.weekly_reminder_enabled} onChange={v => setCfg({ ...cfg, weekly_reminder_enabled: v })} testid="monday-enabled" />
          </div>
          <div>
            <Label>Stale threshold (days without update)</Label>
            <Input data-testid="monday-days" type="number" min="1" max="60" className="max-w-[120px] mt-1"
              value={cfg.weekly_reminder_stale_days} onChange={e => setCfg({ ...cfg, weekly_reminder_stale_days: parseInt(e.target.value) || 7 })} />
            <p className="text-xs text-[#3D2817]/50 mt-1">Sent to the address configured in NOTIFICATION_EMAIL.</p>
          </div>
        </Section>
      </div>

      <div className="mt-8 flex items-center justify-between gap-3 max-w-5xl">
        <Button variant="outline" onClick={runNow} disabled={running} data-testid="automations-run">
          <Send className="w-4 h-4 mr-2" /> {running ? 'Running…' : 'Run all automations now'}
        </Button>
        <Button onClick={save} disabled={saving} data-testid="automations-save" className="bg-[#3D2817] hover:bg-[#C5A059] text-white">
          <Bot className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save settings'}
        </Button>
      </div>
    </div>
  );
}
